import React from 'react';
import { colors } from '../utils/colors';
import type { Teacher } from '../utils/types';

interface Props {
  teacher: Teacher;
  onBack: () => void;
  onLogout: () => void;
}

const formatDob = (dob?: string) => {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return dob;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' });
};

const capitalize = (s?: string) =>
  s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : '—';

const Row: React.FC<{ label: string; value?: string }> = ({ label, value }) => (
  <div style={{ display: 'flex', flexDirection: 'column', gap: 2, padding: '12px 0', borderBottom: `1px solid ${colors.borderLight}` }}>
    <div style={{ fontSize: 11, fontWeight: 700, color: colors.textLight, letterSpacing: '0.6px' }}>
      {label}
    </div>
    <div style={{ fontSize: 15, fontWeight: 500, color: colors.text }}>
      {value || '—'}
    </div>
  </div>
);

const SectionTitle: React.FC<{ title: string }> = ({ title }) => (
  <div style={{
    fontSize: 11, fontWeight: 700, color: colors.primary,
    letterSpacing: '0.8px', marginTop: 20, marginBottom: 4,
  }}>
    {title}
  </div>
);

const ProfileScreen: React.FC<Props> = ({ teacher, onBack, onLogout }) => {
  const roleLabel =
    teacher.role === 'class_teacher' ? 'Class Teacher'
    : teacher.role === 'admin' ? 'Admin'
    : 'Subject Teacher';

  return (
    <div style={{
      minHeight: '100dvh', background: '#fff',
      display: 'flex', flexDirection: 'column',
      fontFamily: "'Inter', sans-serif",
    }}>
      {/* Header */}
      <div style={{
        padding: '52px 20px 16px',
        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        borderBottom: `1px solid ${colors.borderLight}`,
      }}>
        <button onClick={onBack} style={{
          background: 'none', border: 'none', cursor: 'pointer', padding: 0,
          display: 'flex', alignItems: 'center', gap: 6,
          fontSize: 16, fontWeight: 500, color: colors.text,
        }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={colors.text} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <line x1="19" y1="12" x2="5" y2="12"/>
            <polyline points="12 19 5 12 12 5"/>
          </svg>
          Profile
        </button>
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
          stroke={colors.primary} strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2"/>
          <circle cx="12" cy="7" r="4"/>
        </svg>
      </div>

      {/* Scrollable content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '0 20px 120px' }}>

        {/* Avatar + name card */}
        <div style={{
          display: 'flex', alignItems: 'center', gap: 16,
          padding: '20px 0 16px',
          borderBottom: `1px solid ${colors.borderLight}`,
        }}>
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            background: '#C4933A',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 24, fontWeight: 800, color: '#fff', flexShrink: 0,
          }}>
            {teacher.name.charAt(0)}
          </div>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: colors.text, lineHeight: 1.2 }}>
              {teacher.name}
            </div>
            <div style={{
              display: 'inline-block', marginTop: 6,
              background: colors.primaryBg, borderRadius: 20,
              padding: '4px 12px',
              fontSize: 12, fontWeight: 700, color: colors.primary,
              letterSpacing: '0.3px',
            }}>
              {roleLabel}
            </div>
          </div>
        </div>

        {/* Contact */}
        <SectionTitle title="CONTACT" />
        <Row label="Email" value={teacher.email} />
        <Row label="Phone" value={teacher.phone} />

        {/* Address */}
        <SectionTitle title="ADDRESS" />
        <Row label="Address" value={teacher.address} />

        {/* Academic */}
        <SectionTitle title="ACADEMIC" />
        <Row label="Highest Qualification" value={teacher.qualification} />
        <Row label="Expertise" value={teacher.expertise} />
        {teacher.otherExpertise && (
          <Row label="Other Expertise" value={teacher.otherExpertise} />
        )}

        {/* Personal */}
        <SectionTitle title="PERSONAL" />
        <Row label="Date of Birth" value={formatDob(teacher.dob)} />
        <Row label="Gender" value={capitalize(teacher.gender)} />
      </div>

      {/* Fixed bottom logout button */}
      <div style={{
        position: 'fixed', left: 0, right: 0, bottom: 0,
        padding: '12px 20px 36px',
        background: 'rgba(255,255,255,0.92)',
        backdropFilter: 'blur(12px)',
        WebkitBackdropFilter: 'blur(12px)',
      }}>
        <button
          onClick={onLogout}
          style={{
            width: '100%', padding: '15px',
            background: colors.dangerBg,
            border: `1.5px solid ${colors.dangerBorder}`,
            borderRadius: 14,
            fontSize: 15, fontWeight: 700,
            color: colors.danger,
            cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
          }}
        >
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
            stroke={colors.danger} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M9 21H5a2 2 0 01-2-2V5a2 2 0 012-2h4"/>
            <polyline points="16 17 21 12 16 7"/>
            <line x1="21" y1="12" x2="9" y2="12"/>
          </svg>
          Sign Out
        </button>
      </div>
    </div>
  );
};

export default ProfileScreen;
